import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save, Eye, ChevronDown, ChevronUp, Star, LayoutTemplate, LogOut } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const LandingPageEditor = () => {
  const { user, logout } = useAuth();
  const isMasterAdmin = user?.role === 'master_admin';
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: 'Nexus Landing Page',
    description: 'High-quality landing page captured from the Nexus CMS landing page design.',
    category: 'landing',
    thumbnail_url: '',
    is_system: true,
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    features: false,
    pricing: true,
    comparison: false,
    testimonials: false,
    integrations: false,
    enterprise: false,
    cta: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/cms/landing-page');
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load landing page settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/landing-page', settings);
      toast.success('Landing page saved successfully!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Please give the template a name');
      return;
    }
    setSavingTemplate(true);
    try {
      // Persist current edits first so the template reflects what the user sees
      await api.put('/cms/landing-page', settings);
      const res = await api.post('/templates/from-landing-page', {
        name: templateForm.name.trim(),
        description: templateForm.description.trim() || undefined,
        category: templateForm.category || 'landing',
        thumbnail_url: templateForm.thumbnail_url.trim() || undefined,
        is_system: !!templateForm.is_system,
      });
      const blockCount = (res.data?.blocks || []).length;
      const scope = res.data?.is_system ? 'System Template (visible to all tenants)' : 'Tenant Template';
      toast.success(`Saved "${res.data?.name}" as ${scope} with ${blockCount} blocks`);
      setTemplateDialogOpen(false);
    } catch (error) {
      console.error('Failed to save as template:', error);
      const msg = error?.response?.data?.detail || 'Failed to save as template';
      toast.error(msg);
    } finally {
      setSavingTemplate(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateHero = (field, value) => {
    setSettings(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const updateCta = (field, value) => {
    setSettings(prev => ({
      ...prev,
      cta: { ...prev.cta, [field]: value }
    }));
  };

  // Feature management
  const addFeature = () => {
    const newFeature = {
      feature_id: uuidv4(),
      icon: 'star',
      title: 'New Feature',
      description: 'Feature description',
      order: settings.features?.length || 0
    };
    setSettings(prev => ({
      ...prev,
      features: [...(prev.features || []), newFeature]
    }));
  };

  const updateFeature = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      features: prev.features.map(f => 
        f.feature_id === id ? { ...f, [field]: value } : f
      )
    }));
  };

  const removeFeature = (id) => {
    setSettings(prev => ({
      ...prev,
      features: prev.features.filter(f => f.feature_id !== id)
    }));
  };

  // Pricing tier management
  const addPricingTier = () => {
    const newTier = {
      tier_id: uuidv4(),
      name: 'New Tier',
      description: 'Plan description',
      price_monthly: 0,
      price_yearly: 0,
      currency: 'usd',
      features: [{ text: 'Feature 1', included: true }],
      cta_text: 'Get Started',
      cta_url: '#',
      is_popular: false,
      order: settings.pricing_tiers?.length || 0
    };
    setSettings(prev => ({
      ...prev,
      pricing_tiers: [...(prev.pricing_tiers || []), newTier]
    }));
  };

  const updateTier = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers.map(t => 
        t.tier_id === id ? { ...t, [field]: value } : t
      )
    }));
  };

  const removeTier = (id) => {
    setSettings(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers.filter(t => t.tier_id !== id)
    }));
  };

  const addTierFeature = (tierId) => {
    setSettings(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers.map(t => 
        t.tier_id === tierId 
          ? { ...t, features: [...(t.features || []), { text: 'New feature', included: true }] }
          : t
      )
    }));
  };

  const updateTierFeature = (tierId, index, field, value) => {
    setSettings(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers.map(t => {
        if (t.tier_id !== tierId) return t;
        const features = [...t.features];
        features[index] = { ...features[index], [field]: value };
        return { ...t, features };
      })
    }));
  };

  const removeTierFeature = (tierId, index) => {
    setSettings(prev => ({
      ...prev,
      pricing_tiers: prev.pricing_tiers.map(t => {
        if (t.tier_id !== tierId) return t;
        return { ...t, features: t.features.filter((_, i) => i !== index) };
      })
    }));
  };

  // Plan comparison matrix management
  const setComparisonField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const addComparisonRow = () => {
    setSettings(prev => ({
      ...prev,
      comparison_rows: [...(prev.comparison_rows || []), { label: 'New feature', values: {} }]
    }));
  };

  const updateComparisonRowLabel = (index, label) => {
    setSettings(prev => ({
      ...prev,
      comparison_rows: (prev.comparison_rows || []).map((r, i) => i === index ? { ...r, label } : r)
    }));
  };

  const updateComparisonCell = (index, tierId, value) => {
    setSettings(prev => ({
      ...prev,
      comparison_rows: (prev.comparison_rows || []).map((r, i) =>
        i === index ? { ...r, values: { ...(r.values || {}), [tierId]: value } } : r)
    }));
  };

  const removeComparisonRow = (index) => {
    setSettings(prev => ({
      ...prev,
      comparison_rows: (prev.comparison_rows || []).filter((_, i) => i !== index)
    }));
  };

  // Seed the editable matrix from the union of every tier's feature list (case-
  // insensitive), defaulting each cell to that tier's included state (yes/no).
  // Keeps ALL rows (incl. all-✗ ones) so the admin can correct them. This is the
  // "start with auto" behaviour — the list is then fully editable.
  const generateComparisonFromFeatures = () => {
    const tiers = settings.pricing_tiers || [];
    const seen = new Map(); // normalized key -> display label
    tiers.forEach(t => (t.features || []).forEach(f => {
      const label = (f.text || '').trim();
      const key = label.toLowerCase();
      if (key && !seen.has(key)) seen.set(key, label);
    }));
    const rows = Array.from(seen.entries()).map(([key, label]) => {
      const values = {};
      tiers.forEach(t => {
        const has = (t.features || []).some(f => (f.text || '').trim().toLowerCase() === key && f.included);
        values[t.tier_id] = has ? 'yes' : 'no';
      });
      return { label, values };
    });
    setSettings(prev => ({ ...prev, comparison_rows: rows, comparison_mode: 'custom' }));
    toast.success(`Started from auto: ${rows.length} rows — now edit any cell`);
  };

  // Mode change: when switching to Custom with no rows yet, auto-seed from the
  // auto list so the editable matrix is never empty.
  const handleComparisonModeChange = (v) => {
    if (v === 'custom' && !(settings?.comparison_rows || []).length) {
      generateComparisonFromFeatures();
    } else {
      setComparisonField('comparison_mode', v);
    }
  };


  // Testimonial management
  const addTestimonial = () => {
    const newTestimonial = {
      testimonial_id: uuidv4(),
      quote: 'Customer testimonial quote',
      author_name: 'Customer Name',
      author_title: 'Title',
      author_company: 'Company',
      author_image: null,
      rating: 5,
      order: settings.testimonials?.length || 0
    };
    setSettings(prev => ({
      ...prev,
      testimonials: [...(prev.testimonials || []), newTestimonial]
    }));
  };

  const updateTestimonial = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      testimonials: prev.testimonials.map(t => 
        t.testimonial_id === id ? { ...t, [field]: value } : t
      )
    }));
  };

  const removeTestimonial = (id) => {
    setSettings(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter(t => t.testimonial_id !== id)
    }));
  };

  // Integration partner management
  const addPartner = () => {
    const newPartner = {
      partner_id: uuidv4(),
      name: 'Partner Name',
      logo_url: '',
      website_url: '',
      description: '',
      order: settings.integration_partners?.length || 0
    };
    setSettings(prev => ({
      ...prev,
      integration_partners: [...(prev.integration_partners || []), newPartner]
    }));
  };

  const updatePartner = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      integration_partners: prev.integration_partners.map(p => 
        p.partner_id === id ? { ...p, [field]: value } : p
      )
    }));
  };

  const removePartner = (id) => {
    setSettings(prev => ({
      ...prev,
      integration_partners: prev.integration_partners.filter(p => p.partner_id !== id)
    }));
  };

  // Enterprise partner management
  const addEnterprisePartner = () => {
    const newPartner = {
      partner_id: uuidv4(),
      name: 'Partner Name',
      logo_url: '',
      website_url: '',
      description: '',
      order: settings.enterprise_partners?.length || 0
    };
    setSettings(prev => ({
      ...prev,
      enterprise_partners: [...(prev.enterprise_partners || []), newPartner]
    }));
  };

  const updateEnterprisePartner = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      enterprise_partners: prev.enterprise_partners.map(p => 
        p.partner_id === id ? { ...p, [field]: value } : p
      )
    }));
  };

  const removeEnterprisePartner = (id) => {
    setSettings(prev => ({
      ...prev,
      enterprise_partners: prev.enterprise_partners.filter(p => p.partner_id !== id)
    }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Landing Page Editor</h1>
              <p className="text-slate-600 mt-1">Customize your public CMS landing page</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" asChild data-testid="landing-preview-btn">
                <a href="/" target="_blank" rel="noopener noreferrer">
                  <Eye size={18} className="mr-2" />
                  Preview
                </a>
              </Button>
              {isMasterAdmin && (
                <Button
                  variant="outline"
                  onClick={() => setTemplateDialogOpen(true)}
                  data-testid="save-as-system-template-btn"
                  title="Save this landing page as a reusable Page Builder template"
                >
                  <LayoutTemplate size={18} className="mr-2" />
                  Save as System Template
                </Button>
              )}
              <Button onClick={handleSave} disabled={saving} data-testid="save-landing-btn">
                <Save size={18} className="mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <div className="w-px h-6 bg-slate-200 mx-1" />
              <Button
                onClick={logout}
                variant="ghost"
                className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                data-testid="editor-logout-button"
                title="Logout"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Section Toggles */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Section Visibility</CardTitle>
              <CardDescription>Toggle which sections appear on the landing page</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {[
                  { key: 'show_features', label: 'Features' },
                  { key: 'show_pricing', label: 'Pricing' },
                  { key: 'show_comparison', label: 'Compare Table' },
                  { key: 'show_testimonials', label: 'Testimonials' },
                  { key: 'show_integrations', label: 'Integrations' },
                  { key: 'show_enterprise_partners', label: 'Enterprise' },
                  { key: 'show_cta', label: 'CTA Section' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">{label}</span>
                    <Switch
                      checked={settings?.[key] ?? true}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, [key]: checked }))}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hero Section */}
          <Card className="mb-6">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection('hero')}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Hero Section</CardTitle>
                {expandedSections.hero ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.hero && (
              <CardContent className="space-y-4">
                {/* Formatting Help */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <p className="font-medium text-blue-800 mb-1">💡 Text Formatting Tips:</p>
                  <ul className="text-blue-700 space-y-1 text-xs">
                    <li>• Use <code className="bg-blue-100 px-1 rounded">{`<br>`}</code> to add a line break</li>
                    <li>• Use <code className="bg-blue-100 px-1 rounded">&amp;nbsp;</code> between words to keep them together (non-breaking space)</li>
                    <li>• Example: <code className="bg-blue-100 px-1 rounded">Build Amazing{`<br>`}Websites with Nexus&amp;nbsp;CMS</code></li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <Label>Headline</Label>
                  <Textarea
                    value={settings?.hero?.headline || ''}
                    onChange={(e) => updateHero('headline', e.target.value)}
                    rows={2}
                    placeholder="Build Amazing<br>Websites with Nexus&nbsp;CMS"
                    className="font-mono text-sm"
                  />
                  {/* Live Preview */}
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-400 mb-1">Preview:</p>
                    <h3 
                      className="text-xl font-bold text-white"
                      dangerouslySetInnerHTML={{ __html: (settings?.hero?.headline || '').replace(/&nbsp;/g, '\u00A0') }}
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        value={settings?.hero?.background_color || '#1e293b'}
                        onChange={(e) => updateHero('background_color', e.target.value)}
                      />
                      <input
                        type="color"
                        value={settings?.hero?.background_color || '#1e293b'}
                        onChange={(e) => updateHero('background_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        value={settings?.hero?.text_color || '#ffffff'}
                        onChange={(e) => updateHero('text_color', e.target.value)}
                      />
                      <input
                        type="color"
                        value={settings?.hero?.text_color || '#ffffff'}
                        onChange={(e) => updateHero('text_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Subheadline</Label>
                  <Textarea
                    value={settings?.hero?.subheadline || ''}
                    onChange={(e) => updateHero('subheadline', e.target.value)}
                    rows={2}
                    className="font-mono text-sm"
                    placeholder="The all-in-one CMS platform<br>for modern web experiences"
                  />
                  {/* Live Preview */}
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-400 mb-1">Preview:</p>
                    <p 
                      className="text-sm text-slate-700"
                      dangerouslySetInnerHTML={{ __html: (settings?.hero?.subheadline || '').replace(/&nbsp;/g, '\u00A0') }}
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary CTA Text</Label>
                    <Input
                      value={settings?.hero?.cta_primary_text || ''}
                      onChange={(e) => updateHero('cta_primary_text', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary CTA Text</Label>
                    <Input
                      value={settings?.hero?.cta_secondary_text || ''}
                      onChange={(e) => updateHero('cta_secondary_text', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Pricing Tiers Section */}
          <Card className="mb-6">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection('pricing')}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Pricing Tiers</CardTitle>
                {expandedSections.pricing ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.pricing && (
              <CardContent className="space-y-4">
                {settings?.pricing_tiers?.map((tier, index) => (
                  <div key={tier.tier_id} className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <GripVertical size={16} className="text-slate-400" />
                        <span className="font-semibold">{tier.name}</span>
                        {tier.is_popular && (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                            Popular
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTier(tier.tier_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div className="space-y-2">
                        <Label>Tier Name</Label>
                        <Input
                          value={tier.name}
                          onChange={(e) => updateTier(tier.tier_id, 'name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Monthly Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={tier.price_monthly}
                          onChange={(e) => updateTier(tier.tier_id, 'price_monthly', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Yearly Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={tier.price_yearly}
                          onChange={(e) => updateTier(tier.tier_id, 'price_yearly', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <Label>Description</Label>
                      <Input
                        value={tier.description}
                        onChange={(e) => updateTier(tier.tier_id, 'description', e.target.value)}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <Label>CTA Button Text</Label>
                        <Input
                          value={tier.cta_text}
                          onChange={(e) => updateTier(tier.tier_id, 'cta_text', e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-2 pt-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={tier.is_popular}
                            onChange={(e) => updateTier(tier.tier_id, 'is_popular', e.target.checked)}
                            className="rounded"
                            data-testid={`tier-popular-${tier.tier_id}`}
                          />
                          <span className="text-sm">Mark as Popular</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!tier.contact_sales}
                            onChange={(e) => updateTier(tier.tier_id, 'contact_sales', e.target.checked)}
                            className="rounded"
                            data-testid={`tier-contact-sales-${tier.tier_id}`}
                          />
                          <span className="text-sm">Show "Contact us" instead of price</span>
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Features</Label>
                        <Button size="sm" variant="outline" onClick={() => addTierFeature(tier.tier_id)}>
                          <Plus size={14} className="mr-1" /> Add Feature
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {tier.features?.map((feature, fIndex) => (
                          <div key={fIndex} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={feature.included}
                              onChange={(e) => updateTierFeature(tier.tier_id, fIndex, 'included', e.target.checked)}
                              className="rounded"
                            />
                            <Input
                              value={feature.text}
                              onChange={(e) => updateTierFeature(tier.tier_id, fIndex, 'text', e.target.value)}
                              className="flex-1"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTierFeature(tier.tier_id, fIndex)}
                              className="text-red-500"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                <Button onClick={addPricingTier} variant="outline" className="w-full">
                  <Plus size={18} className="mr-2" /> Add Pricing Tier
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Plan Comparison Table Section */}
          <Card className="mb-6">
            <CardHeader
              className="cursor-pointer"
              onClick={() => toggleSection('comparison')}
              data-testid="section-toggle-comparison"
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Plan Comparison Table</CardTitle>
                  <CardDescription>The "Compare plans" table shown below your pricing cards</CardDescription>
                </div>
                {expandedSections.comparison ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.comparison && (
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium">Show comparison table</span>
                  <Switch
                    checked={settings?.show_comparison ?? true}
                    onCheckedChange={(checked) => setComparisonField('show_comparison', checked)}
                    data-testid="comparison-show-toggle"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select
                    value={settings?.comparison_mode || 'auto'}
                    onValueChange={handleComparisonModeChange}
                  >
                    <SelectTrigger data-testid="comparison-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto — derive from each tier's feature list</SelectItem>
                      <SelectItem value="custom">Custom — edit the matrix below</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Auto marks a feature with an ✗ for any tier that doesn't list it (can be misleading).
                    Custom lets you set each cell yourself — type <strong>yes</strong> / <strong>no</strong> for ✓ / ✗, or any text (e.g. "Unlimited", "5 GB").
                  </p>
                </div>

                {settings?.comparison_mode === 'custom' && (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer text-sm p-3 bg-indigo-50 rounded-lg" data-testid="comparison-start-with-auto-label">
                      <input
                        type="checkbox"
                        checked={(settings?.comparison_rows || []).length > 0}
                        onChange={(e) => { if (e.target.checked) generateComparisonFromFeatures(); }}
                        className="rounded"
                        data-testid="comparison-start-with-auto"
                      />
                      <span><strong>Start with the auto list</strong> — pre-fill rows from your tier features, then edit any cell freely</span>
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="outline" onClick={generateComparisonFromFeatures} data-testid="comparison-generate-btn">
                        Reset rows from auto list
                      </Button>
                      <Button size="sm" variant="outline" onClick={addComparisonRow} data-testid="comparison-add-row-btn">
                        <Plus size={14} className="mr-1" /> Add row
                      </Button>
                    </div>

                    {(settings?.comparison_rows || []).length === 0 ? (
                      <p className="text-sm text-slate-500 italic">No rows yet. Click "Generate rows from tier features" to start, then edit any cell.</p>
                    ) : (
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50 border-b">
                              <th className="text-left p-2 font-semibold text-slate-700 min-w-[160px]">Feature</th>
                              {(settings?.pricing_tiers || []).map((t) => (
                                <th key={t.tier_id} className="text-center p-2 font-semibold text-slate-700 min-w-[110px]">{t.name}</th>
                              ))}
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(settings?.comparison_rows || []).map((row, rIdx) => (
                              <tr key={rIdx} className="border-b last:border-0">
                                <td className="p-2">
                                  <Input
                                    value={row.label}
                                    onChange={(e) => updateComparisonRowLabel(rIdx, e.target.value)}
                                    data-testid={`comparison-row-label-${rIdx}`}
                                  />
                                </td>
                                {(settings?.pricing_tiers || []).map((t) => (
                                  <td key={t.tier_id} className="p-2">
                                    <Input
                                      value={(row.values && row.values[t.tier_id]) || ''}
                                      placeholder="yes / no / text"
                                      onChange={(e) => updateComparisonCell(rIdx, t.tier_id, e.target.value)}
                                      className="text-center"
                                      data-testid={`comparison-cell-${rIdx}-${t.tier_id}`}
                                    />
                                  </td>
                                ))}
                                <td className="p-2 text-center">
                                  <Button variant="ghost" size="sm" onClick={() => removeComparisonRow(rIdx)} className="text-red-500" data-testid={`comparison-remove-row-${rIdx}`}>
                                    <Trash2 size={14} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            )}
          </Card>


          {/* Features Section */}
          <Card className="mb-6">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection('features')}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Features</CardTitle>
                {expandedSections.features ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.features && (
              <CardContent className="space-y-4">
                {settings?.features?.map((feature) => (
                  <div key={feature.feature_id} className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <GripVertical size={16} className="text-slate-400" />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature(feature.feature_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={feature.title}
                          onChange={(e) => updateFeature(feature.feature_id, 'title', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Icon</Label>
                        <Select
                          value={feature.icon}
                          onValueChange={(value) => updateFeature(feature.feature_id, 'icon', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="star">Star</SelectItem>
                            <SelectItem value="layout">Layout</SelectItem>
                            <SelectItem value="palette">Palette</SelectItem>
                            <SelectItem value="globe">Globe</SelectItem>
                            <SelectItem value="zap">Zap</SelectItem>
                            <SelectItem value="shield">Shield</SelectItem>
                            <SelectItem value="mail">Mail</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label>Description</Label>
                      <Textarea
                        value={feature.description}
                        onChange={(e) => updateFeature(feature.feature_id, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
                <Button onClick={addFeature} variant="outline" className="w-full">
                  <Plus size={18} className="mr-2" /> Add Feature
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Testimonials Section */}
          <Card className="mb-6">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection('testimonials')}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Testimonials</CardTitle>
                {expandedSections.testimonials ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.testimonials && (
              <CardContent className="space-y-4">
                {settings?.testimonials?.map((testimonial) => (
                  <div key={testimonial.testimonial_id} className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(n => (
                          <button
                            key={n}
                            onClick={() => updateTestimonial(testimonial.testimonial_id, 'rating', n)}
                            className="focus:outline-none"
                          >
                            <Star 
                              size={18} 
                              className={n <= testimonial.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'} 
                            />
                          </button>
                        ))}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTestimonial(testimonial.testimonial_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    <div className="space-y-2 mb-4">
                      <Label>Quote</Label>
                      <Textarea
                        value={testimonial.quote}
                        onChange={(e) => updateTestimonial(testimonial.testimonial_id, 'quote', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Author Name</Label>
                        <Input
                          value={testimonial.author_name}
                          onChange={(e) => updateTestimonial(testimonial.testimonial_id, 'author_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={testimonial.author_title}
                          onChange={(e) => updateTestimonial(testimonial.testimonial_id, 'author_title', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Company</Label>
                        <Input
                          value={testimonial.author_company}
                          onChange={(e) => updateTestimonial(testimonial.testimonial_id, 'author_company', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button onClick={addTestimonial} variant="outline" className="w-full">
                  <Plus size={18} className="mr-2" /> Add Testimonial
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Integration Partners Section */}
          <Card className="mb-6">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection('integrations')}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Integration Partners</CardTitle>
                {expandedSections.integrations ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.integrations && (
              <CardContent className="space-y-4">
                {settings?.integration_partners?.map((partner) => (
                  <div key={partner.partner_id} className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">{partner.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePartner(partner.partner_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Partner Name</Label>
                        <Input
                          value={partner.name}
                          onChange={(e) => updatePartner(partner.partner_id, 'name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Logo URL</Label>
                        <Input
                          value={partner.logo_url}
                          onChange={(e) => updatePartner(partner.partner_id, 'logo_url', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Website URL</Label>
                        <Input
                          value={partner.website_url}
                          onChange={(e) => updatePartner(partner.partner_id, 'website_url', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={partner.description}
                          onChange={(e) => updatePartner(partner.partner_id, 'description', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button onClick={addPartner} variant="outline" className="w-full">
                  <Plus size={18} className="mr-2" /> Add Integration Partner
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Enterprise Partners Section */}
          <Card className="mb-6">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection('enterprise')}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Enterprise Partners</CardTitle>
                {expandedSections.enterprise ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.enterprise && (
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600 mb-4">
                  Featured enterprise partners displayed prominently on the landing page
                </p>
                {settings?.enterprise_partners?.map((partner) => (
                  <div key={partner.partner_id} className="border rounded-lg p-4 bg-gradient-to-r from-indigo-50 to-purple-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                          {partner.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-semibold text-slate-900">{partner.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEnterprisePartner(partner.partner_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Partner Name</Label>
                        <Input
                          value={partner.name}
                          onChange={(e) => updateEnterprisePartner(partner.partner_id, 'name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Logo URL (optional)</Label>
                        <Input
                          value={partner.logo_url}
                          onChange={(e) => updateEnterprisePartner(partner.partner_id, 'logo_url', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Website URL</Label>
                        <Input
                          value={partner.website_url}
                          onChange={(e) => updateEnterprisePartner(partner.partner_id, 'website_url', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={partner.description}
                          onChange={(e) => updateEnterprisePartner(partner.partner_id, 'description', e.target.value)}
                          placeholder="Brief description of the partner"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button onClick={addEnterprisePartner} variant="outline" className="w-full">
                  <Plus size={18} className="mr-2" /> Add Enterprise Partner
                </Button>
              </CardContent>
            )}
          </Card>

          {/* CTA Section */}
          <Card className="mb-6">
            <CardHeader 
              className="cursor-pointer"
              onClick={() => toggleSection('cta')}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Call to Action Section</CardTitle>
                {expandedSections.cta ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </CardHeader>
            {expandedSections.cta && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Headline</Label>
                  <Textarea
                    value={settings?.cta?.headline || ''}
                    onChange={(e) => updateCta('headline', e.target.value)}
                    rows={2}
                    className="font-mono text-sm"
                    placeholder="Ready to get started?<br>Transform your web&nbsp;presence"
                  />
                  {/* Live Preview */}
                  <div 
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: settings?.cta?.background_color || '#4f46e5' }}
                  >
                    <p className="text-xs text-white/60 mb-1">Preview:</p>
                    <h3 
                      className="text-lg font-bold text-white"
                      dangerouslySetInnerHTML={{ __html: (settings?.cta?.headline || '').replace(/&nbsp;/g, '\u00A0') }}
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        value={settings?.cta?.background_color || '#4f46e5'}
                        onChange={(e) => updateCta('background_color', e.target.value)}
                      />
                      <input
                        type="color"
                        value={settings?.cta?.background_color || '#4f46e5'}
                        onChange={(e) => updateCta('background_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        value={settings?.cta?.text_color || '#ffffff'}
                        onChange={(e) => updateCta('text_color', e.target.value)}
                      />
                      <input
                        type="color"
                        value={settings?.cta?.text_color || '#ffffff'}
                        onChange={(e) => updateCta('text_color', e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Subheadline</Label>
                  <Input
                    value={settings?.cta?.subheadline || ''}
                    onChange={(e) => updateCta('subheadline', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Button Text</Label>
                  <Input
                    value={settings?.cta?.cta_text || ''}
                    onChange={(e) => updateCta('cta_text', e.target.value)}
                  />
                </div>
              </CardContent>
            )}
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg">
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Save as System Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent data-testid="save-as-template-dialog">
          <DialogHeader>
            <DialogTitle>Save Landing Page as Template</DialogTitle>
            <DialogDescription>
              Convert the current CMS Landing Page into a reusable Page Builder template.
              All sections (Hero, Features, Pricing, Testimonials, Integrations, Enterprise
              Partners and CTA) will be turned into editable blocks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g. Nexus Landing Page"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="What this template is best for"
                data-testid="template-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={templateForm.category}
                  onValueChange={(v) => setTemplateForm({ ...templateForm, category: v })}
                >
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landing">Landing</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="portfolio">Portfolio</SelectItem>
                    <SelectItem value="blog">Blog</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Thumbnail URL (optional)</Label>
                <Input
                  value={templateForm.thumbnail_url}
                  onChange={(e) => setTemplateForm({ ...templateForm, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="template-thumbnail-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div>
                <p className="text-sm font-medium text-amber-900">Make available to all tenants</p>
                <p className="text-xs text-amber-700">
                  Turn this on to save as a System Template visible to every tenant in their
                  Templates picker.
                </p>
              </div>
              <Switch
                checked={!!templateForm.is_system}
                onCheckedChange={(v) => setTemplateForm({ ...templateForm, is_system: v })}
                data-testid="template-is-system-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTemplateDialogOpen(false)}
              disabled={savingTemplate}
              data-testid="template-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate}
              data-testid="template-save-confirm-btn"
            >
              {savingTemplate ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPageEditor;
